import type {ReactNode} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import classNames from 'classnames/bind';
import {VariableSizeList as List} from 'react-window';

import {SearchIcon} from '../icons';
import LegacyInput from '../LegacyInput';
import type {InputProps as CommonProps} from '../LegacyInput';
import LoadingIndicator from '../LoadingIndicator';
import Heading from '../TextComponents/Heading';
import Text from '../TextComponents/Text';
import DropdownItem from './DropdownItem';

import styles from './styles.module.scss';

const cx = classNames.bind(styles);

const ITEM_HEIGHT = 44;
const ITEM_WITH_HEADING_HEIGHT = 74;
const REGULAR_DROPDOWN_HEIGHT = 425;
const SMALL_DROPDOWN_HEIGHT = 164;

type DropdownOption = {
  id: string | number;
  label: string;
  onClick: ({term, count}: {term: string; count: number}) => void;
  icon?: ReactNode;
  leftIcon?: ReactNode;
  rightText?: string;
  disabled?: boolean;
  optionDataAutomation?: string;
  leftOptionDataAutomation?: string;
  rightOptionDataAutomation?: string;
};

export type DropdownOptionWithHeading = DropdownOption & {heading?: string};

export type InputProps = Pick<
  CommonProps,
  'placeholder' | 'width' | 'disabled' | 'dataAutomation'
>;

type SearchInputProps = InputProps & {
  dropdownOptions: DropdownOption[];
  noResultsHeader: string;
  noResultsDescription: string;
  clearSearchOnSelect?: boolean;
  freeTextSearchHeader?: string;
  optionsHeader?: string;
  isLoading?: boolean;
  onSearchTermChange?: (searchTerm: string) => void;
  onFreeTextSearch?: (searchTerm: string) => void;
  onClearSearchTerm?: () => void;
  dropdownHeight?: 'small' | 'regular';
  defaultTerm?: string;
  optionDataAutomation?: string;
  clearInputDataAutomation?: string;
  searchRightText?: boolean;
};

// eslint-disable-next-line complexity
const SearchInput = ({
  dropdownOptions,
  noResultsHeader,
  noResultsDescription,
  clearSearchOnSelect,
  freeTextSearchHeader,
  optionsHeader,
  isLoading,
  onSearchTermChange,
  onClearSearchTerm,
  onFreeTextSearch,
  dropdownHeight = 'regular',
  defaultTerm = '',
  searchRightText,
  ...props
}: SearchInputProps): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState(defaultTerm);
  const [hasActiveSearch, setHasActiveSearch] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const popoverRef = useRef<List | null>(null);
  let inputElement: HTMLInputElement | HTMLTextAreaElement | null = null;

  useEffect(() => {
    setSearchTerm(defaultTerm);
  }, [defaultTerm]);

  useEffect(() => {
    if (onSearchTermChange) {
      onSearchTermChange(searchTerm);
    }
  }, [searchTerm, onSearchTermChange]);

  const filteredOptions: DropdownOptionWithHeading[] = useMemo(() => {
    const options: DropdownOptionWithHeading[] = [];

    if (onFreeTextSearch) {
      options.push({
        id: -1,
        label: searchTerm,
        leftIcon: <SearchIcon colour='grey700' />,
        onClick: () => onFreeTextSearch(searchTerm),
        heading: freeTextSearchHeader,
      });
    }
    const searchWords = searchTerm.trim().toLowerCase().split(/\s+/);
    options.push(
      ...dropdownOptions
        .filter((item) => {
          const label = item.label.toLowerCase();
          const rightText = item.rightText?.toLowerCase() ?? '';
          return searchWords.every(
            (word) =>
              label.includes(word) ||
              (searchRightText && rightText.includes(word))
          );
        })
        .map((item, i) => (i === 0 ? {...item, heading: optionsHeader} : item))
    );

    return options;
  }, [
    searchTerm,
    dropdownOptions,
    onFreeTextSearch,
    freeTextSearchHeader,
    optionsHeader,
    searchRightText,
  ]);

  useEffect(() => {
    if (!hasActiveSearch) {
      return;
    }

    // ensure focused item is in view
    if (popoverRef.current) {
      popoverRef.current.scrollToItem(activeIndex, 'smart');
    }

    const onKeyDown = (e: KeyboardEvent) => {
      // https://stackoverflow.com/a/17323608/5915221
      const mod = (n: number, m: number) => ((n % m) + m) % m;

      if (e.key === 'ArrowUp') {
        e.preventDefault(); // prevent cursor from jumping to the start
        setActiveIndex((i) => mod(i - 1, filteredOptions.length));
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault(); // prevent cursor from jumping to the end
        setActiveIndex((i) => mod(i + 1, filteredOptions.length));
        return;
      }

      if (e.key === 'Enter') {
        const option = filteredOptions[activeIndex];
        if (option && !option.disabled) {
          option.onClick({
            term: searchTerm,
            count: filteredOptions.length,
          });
          setSearchTerm(clearSearchOnSelect ? '' : option.label);
          setHasActiveSearch(false);
        }
        return;
      }

      if (e.key === 'Escape') {
        setHasActiveSearch(false);
        return;
      }
    };

    document.addEventListener('keydown', onKeyDown);

    // eslint-disable-next-line consistent-return
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    hasActiveSearch,
    activeIndex,
    filteredOptions,
    searchTerm,
    clearSearchOnSelect,
  ]);

  const dropdownDivStyle = cx(
    hasActiveSearch
      ? ['search-dropdown', `${dropdownHeight}-height`]
      : 'hidden',

    // dropdown is hidden by default in free text search mode
    {hidden: onFreeTextSearch && searchTerm.length === 0}
  );

  const height = Math.min(
    dropdownHeight === 'regular'
      ? REGULAR_DROPDOWN_HEIGHT
      : SMALL_DROPDOWN_HEIGHT,
    filteredOptions
      .map((x) => (x.heading ? ITEM_WITH_HEADING_HEIGHT : ITEM_HEIGHT))
      .reduce((a, b) => a + b, 0)
  );

  return (
    <div className={cx('container')}>
      <LegacyInput
        {...props}
        inputRef={(el: HTMLInputElement | HTMLTextAreaElement | null) => {
          inputElement = el;
        }}
        type='search'
        value={searchTerm}
        onChangeValue={(v: string) => {
          setSearchTerm(v);
          setHasActiveSearch(!!v);
          setActiveIndex(0);
        }}
        onClearInput={() => {
          setSearchTerm(defaultTerm);
          if (inputElement) {
            inputElement.focus();
          }
          onClearSearchTerm?.();
        }}
        onFocus={() => {
          setHasActiveSearch(true);
          setActiveIndex(0);
        }}
        onBlur={() => {
          if (defaultTerm !== '') {
            setSearchTerm(defaultTerm);
          }
          setHasActiveSearch(false);
        }}
        includeBottomMargin={false}
        removeSpellcheck
        validationStyles='hide'
        clearInputType='icon'
        width='fullWidth'
      />
      {isLoading && (
        <div className={dropdownDivStyle}>
          <div className={cx('loading-wrapper')}>
            <LoadingIndicator indicator='hexagons' size='small' />
          </div>
        </div>
      )}
      {filteredOptions.length === 0 && !isLoading && (
        <div className={dropdownDivStyle}>
          <div className={cx('no-results-header')}>
            <Heading as='h6' size='ant' weight='medium'>
              {noResultsHeader}
            </Heading>
          </div>
          <div className={cx('no-results-description')}>
            <Text size='m' colour='grey700'>
              {noResultsDescription}
            </Text>
          </div>
        </div>
      )}
      {filteredOptions.length > 0 && !isLoading && (
        <div className={dropdownDivStyle}>
          <List
            ref={popoverRef}
            width='100%'
            height={height}
            itemCount={filteredOptions.length}
            itemSize={(i: number) =>
              filteredOptions[i] && filteredOptions[i].heading
                ? ITEM_WITH_HEADING_HEIGHT
                : ITEM_HEIGHT
            }
            overscanCount={10}
            itemData={{
              filteredOptions,
              activeIndex,
              onHover: (index: number) => setActiveIndex(index),
              onClick: (index: number) => {
                const item = filteredOptions[index];
                if (item && !item.disabled) {
                  const count = filteredOptions.find(
                    (options) => options.id === -1
                  )
                    ? filteredOptions.length - 1
                    : filteredOptions.length;
                  item.onClick({
                    term: searchTerm,
                    count,
                  });
                  setSearchTerm(clearSearchOnSelect ? '' : item.label);
                  setHasActiveSearch(false);
                }
              },
            }}
          >
            {DropdownItem}
          </List>
        </div>
      )}
    </div>
  );
};

export default SearchInput;
